import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";

/**
 * Global analysis state — survives page navigation.
 * Owns the polling loop so the user can leave /pricing while a job is running,
 * browse the lender directory, etc., and come back to a completed report.
 */
const AnalysisContext = createContext(null);

export function AnalysisProvider({ children }) {
  const [files, setFiles] = useState([]);            // list of File objects user picked
  const [uploaded, setUploaded] = useState([]);      // [{id, name, size}] after upload
  const [jobId, setJobId] = useState(null);
  const [phase, setPhase] = useState("");            // "Uploading…" | "AI parsing…" | "Matching lenders…"
  const [elapsed, setElapsed] = useState(0);
  const [parsed, setParsed] = useState(null);
  const [recs, setRecs] = useState(null);
  const [tierPolicy, setTierPolicy] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("idle");      // idle | uploading | analyzing | complete | failed

  const tickerRef = useRef(null);
  const cancelRef = useRef(false);
  const startTimeRef = useRef(null);

  const isRunning = status === "uploading" || status === "analyzing";

  // Elapsed counter
  useEffect(() => {
    if (!isRunning) {
      if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
      return;
    }
    if (!tickerRef.current) {
      tickerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);
    }
    return () => { if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; } };
  }, [isRunning]);

  const reset = () => {
    cancelRef.current = true;
    setFiles([]); setUploaded([]); setJobId(null); setPhase(""); setElapsed(0);
    setParsed(null); setRecs(null); setError(null); setStatus("idle");
  };

  const cancel = () => {
    cancelRef.current = true;
    setStatus("idle"); setPhase(""); setJobId(null);
    toast.info("Analysis cancelled (it'll keep running in the background; refresh later to see)");
  };

  /**
   * Begin a full analysis. fileList = array of File. baseForm = optional snapshot of
   * the deal-form so we can use parsed data + form for the auto-recommend call.
   * onComplete is called with the parsed payload + form updates.
   */
  const startAnalysis = async (fileList, options = {}) => {
    cancelRef.current = false;
    startTimeRef.current = Date.now();
    setFiles(fileList);
    setUploaded([]); setJobId(null); setParsed(null); setRecs(null); setError(null);
    setElapsed(0);
    setStatus("uploading"); setPhase("Uploading…");

    const ids = [];
    try {
      for (const f of fileList) {
        if (cancelRef.current) return;
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", "bank_statement");
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ids.push({ id: data.id, name: data.original_filename, size: data.size });
      }
      setUploaded(ids);
      toast.success(`${ids.length} statement(s) uploaded — analyzing in background`);
    } catch (e) {
      setError(e.response?.data?.detail || "Upload failed");
      setStatus("failed"); setPhase("");
      return toast.error(e.response?.data?.detail || "Upload failed");
    }

    setStatus("analyzing"); setPhase("AI parsing PDFs…");

    try {
      const { data: kicked } = await api.post("/deals/analyze", ids.map((u) => u.id));
      const jid = kicked.job_id;
      if (!jid) throw new Error("No job id returned");
      setJobId(jid);

      let result = null;
      const startedAt = Date.now();
      let consecutiveErrors = 0;
      while (Date.now() - startedAt < 5 * 60 * 1000) {
        if (cancelRef.current) return;
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: st } = await api.get(`/deals/analyze/${jid}`);
          consecutiveErrors = 0;
          if (st.status === "complete") { result = st.result; break; }
          if (st.status === "failed") throw new Error(st.error || "Analysis failed");
        } catch (pe) {
          consecutiveErrors += 1;
          if (consecutiveErrors > 5) throw pe;
        }
      }
      if (!result) throw new Error("Analysis timed out after 5 min");

      setParsed(result);
      // Pre-warm the litigation check on backend the moment we know the
      // business name, so when the user scrolls to that card the result is
      // already cached locally and renders instantly.
      if (result?.business_name) {
        api.get("/court-search", { params: { business_name: result.business_name } })
          .catch(() => {});
      }
      if (result.error) {
        toast.warning(`AI parser: ${result.error}`);
        setStatus("complete"); setPhase("");
        return;
      }

      // Auto-run lender match with the parsed data
      setPhase("Matching lenders…");
      const form = options.form || {};
      const numerics = ["time_in_business_months","credit_score","requested_amount","requested_term_months","monthly_revenue","avg_daily_balance","negative_days","nsfs"];
      const patch = {};
      if (result.avg_monthly_revenue != null) patch.monthly_revenue = Math.round(result.avg_monthly_revenue);
      const adbVal = result.avg_daily_ending_balance ?? result.avg_daily_balance;
      if (adbVal != null) patch.avg_daily_balance = Math.round(adbVal);
      if (result.negative_days != null) patch.negative_days = result.negative_days;
      if (result.nsfs != null) patch.nsfs = result.nsfs;
      if (result.industry_inferred && !form.industry) patch.industry = result.industry_inferred;

      const payload = { ...form, ...patch };
      numerics.forEach((k) => { payload[k] = (payload[k] === "" || payload[k] == null) ? null : Number(payload[k]); });
      payload.bank_statement_file_ids = ids.map((u) => u.id);
      payload.parsed_metrics = result;
      payload.risk_score = result.risk_score;
      try {
        const { data: rec } = await api.post("/deals/recommend", payload);
        setRecs(rec.recommendations || []);
        setTierPolicy(rec.tier_policy || null);
      } catch {}

      setStatus("complete"); setPhase("");
      toast.success(`✓ Analysis complete · ${result.mca_position_count ?? 0} MCA(s) detected`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Analysis failed");
      setStatus("failed"); setPhase("");
      toast.error(e.response?.data?.detail || e.message || "Analysis failed");
    }
  };

  const value = {
    files, setFiles,
    uploaded, jobId, phase, elapsed,
    parsed, setParsed, recs, setRecs,
    status, isRunning, error,
    startAnalysis, reset, cancel,
  };
  return <AnalysisContext.Provider value={value}>{children}</AnalysisContext.Provider>;
}

export function useAnalysis() {
  const v = useContext(AnalysisContext);
  if (!v) throw new Error("useAnalysis must be used inside AnalysisProvider");
  return v;
}
